import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  X
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useOverlay } from '../../contexts/OverlayContext';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { typography } from '../../styles/typography';
import PrimaryButton from '../ui/PrimaryButton';
import Calendar from './calendar';

const ScheduleCalendar = () => {
  // Log screen view
  useScreenLogging('ScheduleCalendar', {
    component: 'ScheduleCalendar',
    screenType: 'schedule-ops',
  });

  // Helper function to get today's date
  const getToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  const today = getToday();

  // Helper to get today's date formatted for <input type="date" />
  const getTodayInputValue = () => {
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // State for calendar
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(today);

  // Responsive layout state (similar to PatrollingDashboard)
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 768
  );

  // State for Create dropdown
  const [isCreateDropdownOpen, setIsCreateDropdownOpen] = useState(false);
  const createDropdownRef = useRef(null);
  const [selectedCreateOption, setSelectedCreateOption] = useState(null);

  // Schedule form state
  const [scheduleType, setScheduleType] = useState('');
  const [scheduleTitle, setScheduleTitle] = useState('');
  // For multiple date selection we keep a list, and reuse scheduleStartDate as the picker value
  const [scheduleDates, setScheduleDates] = useState([getTodayInputValue()]);
  const [scheduleStartDate, setScheduleStartDate] = useState(
    getTodayInputValue()
  );
  const [scheduleEndDate, setScheduleEndDate] = useState(getTodayInputValue());
  const [scheduleStartTime, setScheduleStartTime] = useState('00:00');
  const [scheduleEndTime, setScheduleEndTime] = useState('08:00');
  const [scheduleEmployees, setScheduleEmployees] = useState([]);
  const [scheduleLocation, setScheduleLocation] = useState('');
  const [scheduleDescription, setScheduleDescription] = useState('');

  // Get overlay functions and data from context
  const { openOverlay, assignedEmployees, setAssignedEmployees } = useOverlay();

  // Create dropdown options
  const createOptions = [
    'Event',
    'Drill',
    'Inspection',
    'routine check',
    'Assessment',
    'Scrutiny',
    'other',
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        createDropdownRef.current &&
        !createDropdownRef.current.contains(event.target)
      ) {
        setIsCreateDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle responsive layout on mobile screens (mirror PatrollingDashboard behavior)
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const isMobile = width < 768; // Mobile breakpoint
      setIsMobileViewport(isMobile);
      setViewportWidth(width);
    };

    // Initial check
    handleResize();

    // Listen to resize events
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Get calendar days for the month
  const getCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay()); // Start from Sunday

    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      const isCurrentMonth = date.getMonth() === month;
      const isToday =
        date.toDateString() === today.toDateString() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear();
      const isSelected =
        selectedDate &&
        date.toDateString() === selectedDate.toDateString();

      days.push({
        date,
        isCurrentMonth,
        isToday,
        isSelected,
      });
    }

    return days;
  };

  // Navigate months
  const navigateMonth = direction => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  // Handle date selection
  const handleDateClick = date => {
    // Update the selected date; the right-hand FullCalendar will use this
    // date to show the corresponding Sunday–Saturday week.
    setSelectedDate(date);
  };

  // Sync local employee state with context
  useEffect(() => {
    if (assignedEmployees && assignedEmployees.length > 0) {
      setScheduleEmployees(assignedEmployees);
    }
  }, [assignedEmployees]);

  // Reset form when closing / after submit
  const resetForm = () => {
    const todayValue = getTodayInputValue();
    setScheduleType('');
    setScheduleTitle('');
    setScheduleDates([todayValue]);
    setScheduleStartDate(todayValue);
    setScheduleEndDate(todayValue);
    setScheduleStartTime('00:00');
    setScheduleEndTime('08:00');
    setScheduleEmployees([]);
    setScheduleLocation('');
    setScheduleDescription('');
    setAssignedEmployees([]);
  };

  // Convert selected date & time into Indian timestamp (IST, GMT+05:30)
  const toISTTimestamp = (dateString, timeString) => {
    if (!dateString || !timeString) return null;
    // Construct an ISO string with +05:30 offset and normalise
    const isoWithOffset = `${dateString}T${timeString}:00+05:30`;
    return new Date(isoWithOffset).toISOString();
  };

  const handleFormSubmit = e => {
    e.preventDefault();

    // Derive overall start/end from the selected dates list (for compatibility)
    const sortedDates =
      scheduleDates && scheduleDates.length
        ? [...scheduleDates].sort()
        : [scheduleStartDate];
    const overallStartDate = sortedDates[0];
    const overallEndDate = sortedDates[sortedDates.length - 1];

    const payload = {
      type: selectedCreateOption,
      scheduleType: selectedCreateOption === 'other' ? scheduleType : null,
      title: scheduleTitle,
      dates: sortedDates,
      startDate: overallStartDate,
      endDate: overallEndDate,
      startTimeIST: toISTTimestamp(overallStartDate, scheduleStartTime),
      endTimeIST: toISTTimestamp(overallEndDate, scheduleEndTime),
      employees: scheduleEmployees,
      location: scheduleLocation,
      description: scheduleDescription,
    };

    console.log(`${selectedCreateOption} form submitted:`, payload);
    // TODO: Replace console.log with actual API call / state update

    resetForm();
    setSelectedCreateOption(null);
  };

  // Get month name
  const getMonthName = () => {
    return currentMonth.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    });
  };

  const calendarDays = getCalendarDays();

  return (
    <div
      className={`flex w-full gap-2 ${
        isMobileViewport ? 'flex-col min-h-screen' : 'flex-row h-screen'
      }`}
    >
        {/* Left Sidebar - styled similar to PatrollingDashboard sidebar card */}
        <div
          className={`${
            isMobileViewport ? 'w-full' : 'w-80'
          } bg-white/10 backdrop-blur-md border border-white/10 rounded-xl shadow-lg p-4 flex flex-col overflow-y-auto hide-scrollbar`}
          style={
            isMobileViewport
              ? {
                  minHeight: '260px',
                }
              : undefined
          }
        >
        {/* Schedule Title */}
        <h2 className={`${typography.h2} text-gray-900 mb-4`}>Schedule</h2>

        {/* Create row: dropdown + optional discard button */}
        <div className="flex items-center gap-2 mb-6">
          {/* Create Button with Dropdown */}
          <div className="relative flex-1" ref={createDropdownRef}>
            <button
              onClick={() => setIsCreateDropdownOpen(!isCreateDropdownOpen)}
              className="w-full bg-green-500 hover:bg-green-600 font-semibold py-2 px-4 rounded-lg flex items-center justify-between transition-colors shadow-md"
              style={{ color: '#ffffff' }}
            >
              <div className="flex items-center gap-2" style={{ color: '#ffffff' }}>
                <Plus
                  className="w-5 h-5"
                  strokeWidth={2.5}
                  style={{ stroke: '#ffffff' }}
                />
                <span style={{ color: '#ffffff' }}>
                  {selectedCreateOption || 'Create'}
                </span>
              </div>
              <ChevronDown
                className={`w-5 h-5 transition-transform duration-200 ${
                  isCreateDropdownOpen ? 'rotate-180' : ''
                }`}
                strokeWidth={2.5}
                style={{ stroke: '#ffffff' }}
              />
            </button>

            {/* Dropdown Menu */}
            {isCreateDropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden">
                {createOptions.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      console.log(`Create ${option}`);
                      setSelectedCreateOption(option);
                      setIsCreateDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-100 transition-colors text-gray-900 font-medium border-b border-gray-100 last:border-b-0"
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Discard selection button */}
          {selectedCreateOption && (
            <button
              type="button"
              onClick={() => {
                resetForm();
                setSelectedCreateOption(null);
              }}
              className="w-10 h-10 flex items-center justify-center rounded-lg bg-red-500 hover:bg-red-600 shadow-md transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          )}
        </div>

        {selectedCreateOption ? (
          /* Form on Left Side for any selected option */
          <form
            className="flex flex-col gap-4 h-full"
            onSubmit={handleFormSubmit}
          >

            {/* Custom schedule type when 'other' is selected */}
            {selectedCreateOption === 'other' && (
              <div className="flex flex-col gap-1">
                <label
                  className="text-sm font-semibold text-green-800"
                  htmlFor="schedule-type"
                >
                  Schedule type
                </label>
                <input
                  id="schedule-type"
                  type="text"
                  value={scheduleType}
                  onChange={e => setScheduleType(e.target.value)}
                  placeholder="Enter Schedule type"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white/80 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            )}

            {/* Title */}
            <div className="flex flex-col gap-1">
              <label
                className="text-sm font-semibold text-green-800"
                htmlFor="schedule-title"
              >
                Title
              </label>
              <input
                id="schedule-title"
                type="text"
                value={scheduleTitle}
                onChange={e => setScheduleTitle(e.target.value)}
                placeholder="Enter Title"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white/80 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            {/* Set Date & Time */}
            <div className="flex flex-col gap-2">
              <span className="text-sm font-semibold text-green-800">
                Set Date & Time
              </span>

              {/* Date selection (single or multiple) */}
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <div className="flex-1 relative">
                    <input
                      type="date"
                      value={scheduleStartDate}
                      min={getTodayInputValue()}
                      onChange={e => setScheduleStartDate(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white/80 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    {/* <CalendarIcon className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2" /> */}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const value = scheduleStartDate;
                      const todayStr = getTodayInputValue();
                      if (!value) return;
                      // Prevent adding past dates
                      if (value < todayStr) return;
                      setScheduleDates(prev =>
                        prev.includes(value) ? prev : [...prev, value]
                      );
                    }}
                    className="px-3 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-medium"
                  >
                    Add
                  </button>
                </div>

                {/* Selected dates list */}
                {scheduleDates && scheduleDates.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {scheduleDates
                      .slice()
                      .sort()
                      .map(date => (
                        <div
                          key={date}
                          className="flex items-center gap-1 px-2 py-1 rounded-full bg-green-50 border border-green-200 text-xs text-green-800"
                        >
                          <span>{date}</span>
                          <button
                            type="button"
                            onClick={() =>
                              setScheduleDates(prev =>
                                prev.filter(d => d !== date)
                              )
                            }
                            className="w-4 h-4 flex items-center justify-center rounded-full bg-green-200 text-[10px] leading-none"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* Time range */}
              <div className="flex items-center gap-2">
                <div className="flex-1 relative">
                  <input
                    type="time"
                    value={scheduleStartTime}
                    onChange={e => setScheduleStartTime(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white/80 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  {/* <Clock className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2" /> */}
                </div>
                <span className="text-sm text-gray-700">~</span>
                <div className="flex-1 relative">
                  <input
                    type="time"
                    value={scheduleEndTime}
                    onChange={e => setScheduleEndTime(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white/80 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  {/* <Clock className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2" /> */}
                </div>
              </div>
            </div>

            {/* Employees */}
            <div className="flex flex-col gap-1">
              <span className="text-sm font-semibold text-green-800">
                Employees
              </span>
              <button
                type="button"
                onClick={() => {
                  // Open the employee selection overlay
                  openOverlay('assignEmployees', {
                    employeeType: 'supervisors', // or 'guards' depending on your needs
                    shiftValidationData: {
                      selectedSite: scheduleLocation ? { site_name: scheduleLocation } : null,
                      currentShift: {
                        shift_start_time: scheduleStartTime,
                        shift_end_time: scheduleEndTime
                      },
                      date: scheduleDates && scheduleDates.length > 0 ? scheduleDates[0] : null,
                      startTime: scheduleStartTime,
                      endTime: scheduleEndTime
                    }
                  });
                }}
                className="w-full flex items-center justify-center gap-2 px-3 py-3 rounded-lg bg-gradient-to-r from-white/80 to-green-50 text-sm font-medium text-gray-800 border border-gray-200 shadow-sm hover:from-white hover:to-green-100 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Add Employee</span>
              </button>
              
              {/* Display selected employees */}
              {scheduleEmployees && scheduleEmployees.length > 0 && (
                <div className="mt-2 space-y-2">
                  {scheduleEmployees.map((employee, index) => {
                    const employeeName = typeof employee === 'string' 
                      ? employee 
                      : (employee.user_name || employee.name || employee.display_name || 'Unknown');
                    return (
                      <div
                        key={index}
                        className="flex items-center justify-between px-3 py-2 rounded-lg bg-green-50 border border-green-200"
                      >
                        <span className="text-sm text-green-800">{employeeName}</span>
                        <button
                          type="button"
                          onClick={() => {
                            const updatedEmployees = scheduleEmployees.filter((_, i) => i !== index);
                            setScheduleEmployees(updatedEmployees);
                            setAssignedEmployees(updatedEmployees);
                          }}
                          className="text-green-600 hover:text-green-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Location */}
            <div className="flex flex-col gap-1">
              <label
                className="text-sm font-semibold text-green-800"
                htmlFor="schedule-location"
              >
                Location
              </label>
              <input
                id="schedule-location"
                type="text"
                value={scheduleLocation}
                onChange={e => setScheduleLocation(e.target.value)}
                placeholder="Select Site"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white/80 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            {/* Description */}
            <div className="flex flex-col gap-1">
              <label
                className="text-sm font-semibold text-green-800"
                htmlFor="schedule-description"
              >
                Description
              </label>
              <textarea
                id="schedule-description"
                rows={4}
                value={scheduleDescription}
                onChange={e => setScheduleDescription(e.target.value)}
                placeholder="Enter Description"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white/80 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            {/* Submit */}
            <div className="mt-2 pb-4">
              <PrimaryButton
                type="submit"
                className="w-full h-12 text-base font-semibold bg-green-600 hover:bg-green-700"
              >
                Submit and update
              </PrimaryButton>
            </div>
          </form>
        ) : (
          /* Monthly Calendar on Left Side (default view) */
          <div className="flex-1">
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => navigateMonth('prev')}
                className="p-2 hover:bg-purple-300 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-gray-700" />
              </button>
              <h3 className={`${typography.h4} text-gray-900 font-semibold`}>
                {getMonthName()}
              </h3>
              <button
                onClick={() => navigateMonth('next')}
                className="p-2 hover:bg-purple-300 rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-gray-700" />
              </button>
            </div>

            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                <div
                  key={index}
                  className="text-center text-sm font-semibold text-gray-700 py-1"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day, index) => (
                <button
                  key={index}
                  onClick={() => handleDateClick(day.date)}
                  className={`aspect-square flex items-center justify-center text-sm rounded-lg transition-all ${
                    !day.isCurrentMonth
                      ? 'text-gray-300'
                      : day.isSelected
                        ? 'bg-green-600 text-white font-semibold shadow-md'
                        : day.isToday
                          ? 'bg-purple-200 text-purple-900 border-2 border-purple-400'
                          : 'text-gray-700 hover:bg-purple-200'
                  }`}
                >
                  {day.date.getDate()}
                </button>
              ))}
            </div>
          </div>
        )}
        </div>

        {/* Right Main Content - FullCalendar Widget styled as a glassmorphism card */}
        <div
          className={`${
            isMobileViewport ? 'w-full flex-1' : 'flex-1'
          } bg-white/10 backdrop-blur-md border border-white/10 rounded-xl shadow-lg p-1 overflow-auto`}
          style={
            isMobileViewport
              ? {
                  maxHeight: '480px',
                  height: '100%',
                }
              : undefined
          }
        >
        {/* Timezone Display */}
        {/* <div className="mb-4">
          <span className="text-sm text-gray-600 font-medium">GMT+05:30</span>
        </div> */}

        {/* FullCalendar Component */}
        {/* Pass the selectedDate so the right calendar shows that date's week (Sun–Sat) */}
        <Calendar selectedDate={selectedDate} />
        </div>
      </div>
  );
};

export default ScheduleCalendar;

